import { useMemo, type RefObject } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { toolbarInsertBlockMessages } from '@atlaskit/editor-common/messages';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension/selection-extension-plugin-type';
import TableIcon from '@atlaskit/icon/core/table';
import type { ExtensionConfiguration } from '@atlaskit/editor-plugin-selection-extension/types';

import { expVal } from '@confluence/feature-experiments';

import { buildBaseManifest } from './baseManifest.tsx';
import { getNativeTabsBlockTransform } from './blockTransform.ts';
import { transformSelectedNodeToNativeTabsAdf } from './selectionTransform.ts';

const i18n = defineMessages({
	turnIntoTabs: {
		id: 'native-tabs.selection-extension.turn-into-tabs.ai-non-final',
		defaultMessage: 'Tabs',
		description:
			'Menu item label for converting selected Panel, Expand, or Layout content into Native Tabs.',
	},
});

export const useNativeTabsSelectionExtension = ({
	editorAPIRef,
	isEnabled,
}: {
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
	isEnabled?: boolean;
}): ExtensionConfiguration[] => {
	const intl = useIntl();
	const tabsLabel = intl.formatMessage(i18n.turnIntoTabs);
	const tableLabel = intl.formatMessage(toolbarInsertBlockMessages.table);
	const { icon: NativeTabsIcon, manifest } = buildBaseManifest(intl);
	const extensionTitle = manifest.title;
	const isNativeTabsSelectionExtensionEnabled =
		isEnabled ?? expVal('confluence_native_tabs_m15', 'isEnabled', false);

	return useMemo(() => {
		if (!isNativeTabsSelectionExtensionEnabled) {
			return [];
		}

		return [
			{
				key: 'native-tabs-selection-transform',
				source: 'first-party',
				blockMenu: {
					placement: 'structure',
					getMenuItems: () => {
						const selectedNodeAdf =
							editorAPIRef.current?.selectionExtension?.actions?.getSelectionAdf?.()
								?.selectedNodeAdf;
						const nativeTabsAdf = transformSelectedNodeToNativeTabsAdf(
							selectedNodeAdf,
							extensionTitle,
						);
						const tableAdf = selectedNodeAdf
							? getNativeTabsBlockTransform(intl).transform({
									source: selectedNodeAdf,
									targetTypeName: 'table',
								})?.output[0]
							: undefined;

						return [
							...(nativeTabsAdf
								? [
										{
											key: 'native-tabs-selection-transform-turn-into-tabs',
											label: tabsLabel,
											icon: NativeTabsIcon,
											onClick: () => {
												editorAPIRef.current?.selectionExtension?.actions?.replaceWithAdf(
													nativeTabsAdf,
												);
											},
										},
									]
								: []),
							...(tableAdf
								? [
										{
											key: 'native-tabs-selection-transform-turn-into-table',
											label: tableLabel,
											icon: TableIcon,
											onClick: () => {
												editorAPIRef.current?.selectionExtension?.actions?.replaceWithAdf(tableAdf);
											},
										},
									]
								: []),
						];
					},
				},
			},
		];
	}, [
		NativeTabsIcon,
		editorAPIRef,
		extensionTitle,
		intl,
		isNativeTabsSelectionExtensionEnabled,
		tableLabel,
		tabsLabel,
	]);
};
